import { DatePicker, Form, Input, Switch } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { FormModal } from "@/shared/components/FormModal";
import type { HolidayFormValues, HolidayInput } from "../types";

interface HolidayFormModalProps {
	open: boolean;
	/** Ro'yxat filtri ko'rsatib turgan yil — kiritish shu yil bilan chegaralanadi. */
	year: number;
	isSubmitting: boolean;
	onCancel: () => void;
	onSubmit: (input: HolidayInput) => void;
}

const NEW_HOLIDAY_VALUES = { isWorkday: false } as const;

/**
 * Tanlangan yildan tashqaridagi sana — DatePicker'da bloklanadi.
 *
 * ⚠️ Sababi jimgina nosozlik edi: ro'yxat 2026 ni ko'rsatib turganda
 * 2027-01-01 kiritilsa, muvaffaqiyat toasti chiqardi-yu, yangi qator
 * ko'rinmasdi — u boshqa yilning ro'yxatiga tushgan bo'lardi.
 */
export function isOutsideYear(date: Dayjs, year: number): boolean {
	return date.year() !== year;
}

/**
 * ⚠️ Faqat YARATISH — `PATCH /holidays/{id}` marshruti mavjud emas
 * (`holidays.routes.ts`: `list` · `create` · `delete`). Xato kiritilgan
 * yozuv o'chirilib, qaytadan kiritiladi.
 */
export function HolidayFormModal({
	open,
	year,
	isSubmitting,
	onCancel,
	onSubmit,
}: HolidayFormModalProps) {
	const { t } = useTranslation("schedules");

	const handleSubmit = (values: HolidayFormValues) => {
		onSubmit({
			sana: values.sana.format("YYYY-MM-DD"),
			nom: values.nom,
			tur: values.tur?.trim() ? values.tur.trim() : null,
			isWorkday: values.isWorkday ?? false,
		});
	};

	return (
		<FormModal<HolidayFormValues>
			open={open}
			title={t("holidayForm.createTitle", { year })}
			initialValues={NEW_HOLIDAY_VALUES}
			isSubmitting={isSubmitting}
			onCancel={onCancel}
			onSubmit={handleSubmit}
		>
			<Form.Item
				name="sana"
				label={t("holidayForm.date.label")}
				rules={[{ required: true, message: t("holidayForm.date.required") }]}
				extra={t("holidayForm.date.extra", { year })}
			>
				<DatePicker
					className="w-full"
					disabledDate={(date) => isOutsideYear(date, year)}
					defaultPickerValue={dayjs().year(year)}
				/>
			</Form.Item>

			<Form.Item
				name="nom"
				label={t("holidayForm.name.label")}
				rules={[{ required: true, message: t("holidayForm.name.required") }]}
			>
				<Input placeholder={t("holidayForm.name.placeholder")} />
			</Form.Item>

			<Form.Item name="tur" label={t("holidayForm.type.label")}>
				<Input placeholder={t("holidayForm.type.placeholder")} />
			</Form.Item>

			<Form.Item
				name="isWorkday"
				label={t("holidayForm.isWorkday.label")}
				valuePropName="checked"
				extra={t("holidayForm.isWorkday.extra")}
			>
				<Switch />
			</Form.Item>
		</FormModal>
	);
}
